import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  Layers,
  Award,
  Brain,
  Calendar,
  MapPin,
  FileText,
  Download,
  Eye,
  UserPlus2,
  UserMinus2,
} from "lucide-react";
import { normalizeMediaUrl } from "utils/getImageUrl";
import { getMediaUrl } from "utils/getMediaUrl";
import { LEVEL_OPTIONS } from "list";
import { institutionImages } from "dataClient/institutions";
import { countriesFR } from "data/countriesFR";
import CardActionButtons from "components/CardActionButtons";
import HighlightText from "components/HighlightText";
import axios from "utils/axios";
import { openSnackbar } from "api/snackbar";
import { brand } from "brand/theme";
import formationEmptyIcon from "assets/icons/playlist-01_w.svg";
import moduleEmptyIcon from "assets/icons/play-list_w.svg";
import podcastEmptyIcon from "assets/icons/co-present.svg";
import eventEmptyIcon from "assets/icons/calendar-03.svg";

function EmptyMediaIcon({ src, alt = "" }) {
  return (
    <div className="f2-card-media-icon" aria-hidden="true">
      <img
        src={src}
        alt={alt}
        style={{ filter: brand.iconFilters.accent }}
      />
    </div>
  );
}

function levelLabel(level) {
  if (!level) return null;
  const match = LEVEL_OPTIONS.find(
    (opt) => opt.id === level || opt.name === level,
  );
  return match?.name || level;
}

function organiserName(item) {
  const org = item?.organisateur;
  if (!org) return "";
  if (typeof org === "string") return org;
  if (Array.isArray(org) && org.length) {
    const first = org[0];
    if (typeof first === "string") return first;
    return first?.name || first?.title || "";
  }
  return org?.name || org?.title || "";
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function expertAvatarSrc(person) {
  const raw =
    person?.profile_image || person?.avatar || person?.picture || null;
  if (!raw) return null;
  if (typeof raw === "string" && raw.startsWith("http")) return raw;
  return normalizeMediaUrl(raw);
}

function expertInitials(person) {
  const name =
    person?.name ||
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim() ||
    "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

export function ExpertAvatars({
  people = [],
  emptyLabel = "—",
  max = 2,
}) {
  const list = (people || []).filter(Boolean);
  if (!list.length) {
    return <p className="f2-card-org">{emptyLabel}</p>;
  }
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;

  return (
    <div className="f2-card-avatars" aria-label="Experts">
      {shown.map((person, index) => {
        const src = expertAvatarSrc(person);
        const key = person.id || person.name || index;
        return src ? (
          <img key={key} className="f2-card-avatar" src={src} alt="" />
        ) : (
          <span
            key={key}
            className="f2-card-avatar f2-card-avatar-fallback"
          >
            {expertInitials(person)}
          </span>
        );
      })}
      {extra > 0 && <span className="f2-card-avatar-more">+{extra}</span>}
    </div>
  );
}

const cardMotion = {
  layout: true,
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
};

/** Formation listing card (/formation, favorites, apprentissage, related). */
export function FormationV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  featured = false,
  highlightQuery = "",
  emptyIconSrc = formationEmptyIcon,
}) {
  const category =
    item?.category?.name || item?.Categorie?.name || "Formation";
  const level = levelLabel(item?.level);
  const thumb = item?.thumbnails
    ? normalizeMediaUrl(item.thumbnails)
    : null;
  const modulesCount =
    item?.modules?.modules_count ||
    item?.modules?.allModules?.length ||
    item?.modules_count ||
    0;
  const hasCert = Array.isArray(item?.certificat)
    ? item.certificat.length > 0
    : Boolean(item?.certificat);
  const org = organiserName(item);
  const isFavorite = Boolean(
    item?.is_bookmarked ?? item?.IsFavorite ?? item?.is_favorite,
  );

  return (
    <motion.article
      {...cardMotion}
      className={`f2-card${featured ? " f2-card-featured" : ""}`}
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? (
          <img src={thumb} alt="" />
        ) : (
          <EmptyMediaIcon src={emptyIconSrc} />
        )}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          {modulesCount > 0 && (
            <span className="f2-chip f2-chip-light">
              <Layers size={12} />
              {modulesCount} modules
            </span>
          )}
          {hasCert && (
            <span className="f2-chip f2-chip-light" title="Certifiante">
              <Award size={12} />
            </span>
          )}
          <CardActionButtons
            id={item.id}
            favoriteType="formation"
            editType="formation"
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        <div className="f2-card-media-bottom">
          {item?.formatted_duration && (
            <span className="f2-chip f2-chip-dark">
              <Clock size={12} />
              {item.formatted_duration}
            </span>
          )}
          {level && <span className="f2-chip f2-chip-dark">{level}</span>}
        </div>
      </div>

      <div className="f2-card-body">
        <div className="f2-card-tags">
          <span className="f2-tag">
            <HighlightText text={category} query={highlightQuery} />
          </span>
        </div>
        <h3 className="f2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>
        <p className="f2-card-org">
          <HighlightText
            text={org || "Organisateur"}
            query={highlightQuery}
          />
        </p>
      </div>
    </motion.article>
  );
}

/** Masterclass / podcast listing card. */
export function MasterclassV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  featured = false,
  highlightQuery = "",
}) {
  return (
    <FormationV2Card
      item={item}
      onOpen={onOpen}
      onFavoriteChange={onFavoriteChange}
      isOnFavoritesPage={isOnFavoritesPage}
      featured={featured}
      highlightQuery={highlightQuery}
      emptyIconSrc={podcastEmptyIcon}
    />
  );
}

/** Module listing card. */
export function ModuleV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const category =
    item?.category?.name || item?.formation?.category?.name || "Module";
  const level = levelLabel(item?.level || item?.formation?.level);
  const thumb = item?.thumbnails
    ? normalizeMediaUrl(item.thumbnails)
    : null;
  const formationTitle =
    item?.formation?.title || item?.formation?.name || "Formation";
  const hasQuiz = Boolean(item?.quizzes?.id || item?.quiz);
  const order = item?.order;
  const parentId = item?.formation_id || item?.formation?.id;
  const isFavorite = Boolean(
    item?.is_bookmarked ?? item?.IsFavorite ?? item?.is_favorite,
  );

  const handleOpen = () => {
    if (onOpen) onOpen(item);
  };

  return (
    <motion.article
      {...cardMotion}
      className="f2-card m2-card"
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleOpen();
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? (
          <img src={thumb} alt="" />
        ) : (
          <EmptyMediaIcon src={moduleEmptyIcon} />
        )}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          {order != null && (
            <span className="f2-chip f2-chip-light">N° {order}</span>
          )}
          {hasQuiz && (
            <span className="f2-chip f2-chip-light" title="Quiz">
              <Brain size={12} />
              Quiz
            </span>
          )}
          <CardActionButtons
            id={item.id}
            favoriteType="module"
            editType="module"
            parentId={parentId}
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        <div className="f2-card-media-bottom">
          {level && <span className="f2-chip f2-chip-dark">{level}</span>}
          {item?.parentType === "masterclass" && (
            <span className="f2-chip f2-chip-dark">Masterclass</span>
          )}
        </div>
      </div>

      <div className="f2-card-body">
        <div className="f2-card-tags">
          <span className="f2-tag">
            <HighlightText text={category} query={highlightQuery} />
          </span>
        </div>
        <h3 className="f2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>
        <p className="f2-card-org">
          <HighlightText text={formationTitle} query={highlightQuery} />
        </p>
      </div>
    </motion.article>
  );
}

/** Event listing card (/agenda, favorites, related). */
export function EventV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const typeName = item?.type?.name || "Événement";
  const thumb = item?.image ? normalizeMediaUrl(item.image) : null;
  const people =
    item?.intervenants?.length > 0
      ? item.intervenants
      : item?.experts?.length > 0
        ? item.experts
        : item?.participants || [];
  const participation = item?.participation;
  const nature = item?.event_nature || item?.nature;
  const isFavorite = Boolean(item?.IsFavorite ?? item?.is_favorite);

  return (
    <motion.article
      {...cardMotion}
      className="f2-card"
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty is-event-empty"}`}>
        {thumb ? (
          <img src={thumb} alt="" />
        ) : (
          <EmptyMediaIcon src={eventEmptyIcon} />
        )}
        {thumb ? <div className="f2-card-media-shade" /> : null}

        <div className="f2-card-media-top">
          {participation && (
            <span className="f2-chip f2-chip-light">
              <MapPin size={12} />
              {participation}
            </span>
          )}
          <CardActionButtons
            id={item.id}
            favoriteType="event"
            editType="event"
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        <div className="f2-card-media-bottom">
          {(item?.start_date || item?.end_date) && (
            <span className="f2-chip f2-chip-dark">
              <Calendar size={12} />
              {formatDate(item.start_date || item.end_date)}
            </span>
          )}
          {nature && <span className="f2-chip f2-chip-dark">{nature}</span>}
        </div>
      </div>

      <div className="f2-card-body">
        <div className="f2-card-tags">
          <span className="f2-tag">
            <HighlightText text={typeName} query={highlightQuery} />
          </span>
        </div>
        <h3 className="f2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>
        <ExpertAvatars people={people} />
      </div>
    </motion.article>
  );
}

/** Library document listing card. */
export function LibraryV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const category = item?.category?.name || "Document";
  const thumb = item?.thumbnail
    ? normalizeMediaUrl(item.thumbnail)
    : item?.organization_icon
      ? normalizeMediaUrl(item.organization_icon)
      : null;
  const fileType = item?.file_type
    ? String(item.file_type).toUpperCase()
    : null;
  const isFavorite = Boolean(item?.is_favorite ?? item?.IsFavorite);

  return (
    <motion.article
      {...cardMotion}
      className="f2-card"
      onClick={() => onOpen?.(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(item.id);
      }}
    >
      <div className={`f2-card-media${thumb ? "" : " is-empty"}`}>
        {thumb ? (
          <img src={thumb} alt="" />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(15,23,32,0.35)",
            }}
          >
            <FileText size={48} />
          </div>
        )}
        <div className="f2-card-media-shade" />

        <div className="f2-card-media-top">
          {fileType && (
            <span className="f2-chip f2-chip-light">{fileType}</span>
          )}
          <CardActionButtons
            id={item.id}
            favoriteType="library"
            editType="library"
            initialIsFavorite={isFavorite}
            onFavoriteChange={onFavoriteChange}
            isOnFavoritesPage={isOnFavoritesPage}
            size="small"
            className="f2-card-actions"
          />
        </div>

        <div className="f2-card-media-bottom">
          {item?.date && (
            <span className="f2-chip f2-chip-dark">
              {formatDate(item.date)}
            </span>
          )}
          {item?.download_count != null && (
            <span className="f2-chip f2-chip-dark">
              <Download size={12} />
              {item.download_count}
            </span>
          )}
          {item?.view_count != null && (
            <span className="f2-chip f2-chip-dark">
              <Eye size={12} />
              {item.view_count}
            </span>
          )}
        </div>
      </div>

      <div className="f2-card-body">
        <div className="f2-card-tags">
          <span className="f2-tag">
            <HighlightText text={category} query={highlightQuery} />
          </span>
        </div>
        <h3 className="f2-card-title">
          <HighlightText
            text={item?.name || item?.title}
            query={highlightQuery}
          />
        </h3>
        <ExpertAvatars
          people={item?.experts || []}
          emptyLabel="Bibliothèque"
        />
      </div>
    </motion.article>
  );
}

function getCountryCode(countryValue) {
  if (!countryValue) return null;
  const normalizedValue = countryValue.trim().toUpperCase();
  if (countriesFR.some((c) => c.code === normalizedValue)) {
    return normalizedValue;
  }
  const countryNameMap = {
    morocco: "Maroc",
    maroc: "Maroc",
    france: "France",
    algeria: "Algérie",
    tunisia: "Tunisie",
    spain: "Espagne",
    italy: "Italie",
    germany: "Allemagne",
    belgium: "Belgique",
    canada: "Canada",
    "united states": "États-Unis",
    usa: "États-Unis",
    "united kingdom": "Royaume-Uni",
    uk: "Royaume-Uni",
  };
  const mappedName = countryNameMap[countryValue.toLowerCase()];
  const searchName = mappedName || countryValue;
  const countryObj = countriesFR.find(
    (c) =>
      c.label?.toLowerCase() === searchName.toLowerCase() ||
      c.label?.toLowerCase().includes(searchName.toLowerCase()) ||
      searchName.toLowerCase().includes(c.label?.toLowerCase()),
  );
  return countryObj ? countryObj.code : null;
}

function expertDisplayName({ title, first_name, last_name, full_name }) {
  const prefix =
    title === "DOC" ? "Dr" : title === "PROF" ? "Pr" : "";
  const name =
    `${first_name || ""} ${last_name || ""}`.trim() || full_name || "";
  return `${prefix ? `${prefix} ` : ""}${name}`.trim();
}

/** Expert listing card with follow. */
export function ExpertV2Card({ expert, onOpen, highlightQuery = "" }) {
  const [isFollowed, setIsFollowed] = useState(expert.is_followed);
  const [followerCount, setFollowerCount] = useState(expert.total_followers);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsFollowed(expert.is_followed);
    setFollowerCount(expert.total_followers);
  }, [expert.id, expert.is_followed, expert.total_followers]);

  const name = expertDisplayName(expert);
  const countryCode = getCountryCode(expert.country);
  const countryObj = countryCode
    ? countriesFR.find((c) => c.code === countryCode)
    : null;
  const portrait = expert.profile_image
    ? normalizeMediaUrl(expert.profile_image) || expert.profile_image
    : null;
  const letters = (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");

  const handleFollowToggle = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    const wasFollowed = isFollowed;
    try {
      setIsFollowed(!wasFollowed);
      setFollowerCount((prev) => (wasFollowed ? prev - 1 : prev + 1));
      await axios.post(`/api/experts/follow/${expert.id}/`);
      openSnackbar({
        open: true,
        message: wasFollowed
          ? `Vous ne suivez plus ${name}`
          : `Vous suivez maintenant ${name}`,
        variant: "alert",
        alert: { color: wasFollowed ? "error" : "success" },
        autoHideDuration: 4000,
      });
    } catch (error) {
      setIsFollowed(wasFollowed);
      setFollowerCount(expert.total_followers);
      console.error("Error toggling follow status:", error);
      openSnackbar({
        open: true,
        message: "Une erreur s'est produite lors de la modification du suivi",
        variant: "alert",
        alert: { color: "error" },
        autoHideDuration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.article
      {...cardMotion}
      className="f2-card ex-card"
      onClick={() => onOpen?.(expert.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(expert.id);
      }}
    >
      <CardActionButtons
        id={expert.id}
        editType="expert"
        size="medium"
        className="ex-edit"
      />

      <div
        className={`f2-card-media is-portrait${portrait ? "" : " is-empty"}`}
      >
        {portrait ? (
          <img src={portrait} alt="" />
        ) : (
          <div className="ex-card-portrait-fallback" aria-hidden="true">
            {letters || "?"}
          </div>
        )}
        <div className="f2-card-media-shade" />
      </div>

      <div className="ex-card-body">
        <div className="ex-card-head">
          <h3 className="ex-card-title">
            <HighlightText text={name} query={highlightQuery} />
          </h3>
          {countryCode && (
            <img
              className="ex-card-flag"
              loading="lazy"
              src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
              alt={countryObj?.label || expert.country || ""}
              title={countryObj?.label || expert.country || ""}
            />
          )}
        </div>

        {expert.resume && (
          <p className="ex-card-resume">
            <HighlightText text={expert.resume} query={highlightQuery} />
          </p>
        )}

        <div className="ex-card-foot">
          <span className="ex-followers">
            {followerCount ?? 0} abonné
            {(followerCount ?? 0) !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            className={`ex-follow-btn${isFollowed ? " is-following" : " is-idle"}`}
            onClick={handleFollowToggle}
            disabled={isLoading}
          >
            {isLoading ? (
              "Chargement…"
            ) : isFollowed ? (
              <>
                <UserMinus2 size={16} />
                Abonné
              </>
            ) : (
              <>
                <UserPlus2 size={16} />
                Suivre
              </>
            )}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

/** Institution listing card with follow. */
export function InstitutionV2Card({
  institution,
  onOpen,
  highlightQuery = "",
}) {
  const safeTotalFollowers =
    typeof institution.total_followers === "number" &&
    Number.isFinite(institution.total_followers)
      ? institution.total_followers
      : 0;
  const [isFollowed, setIsFollowed] = useState(
    Boolean(institution.is_followed),
  );
  const [followerCount, setFollowerCount] = useState(safeTotalFollowers);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsFollowed(Boolean(institution.is_followed));
    setFollowerCount(
      typeof institution.total_followers === "number" &&
        Number.isFinite(institution.total_followers)
        ? institution.total_followers
        : 0,
    );
  }, [institution.id, institution.is_followed, institution.total_followers]);

  const logoSrc = institution.logo
    ? getMediaUrl(institution.logo)
    : institutionImages[7];

  const handleFollowToggle = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    const wasFollowed = isFollowed;
    try {
      setIsFollowed(!wasFollowed);
      setFollowerCount((prev) => (wasFollowed ? prev - 1 : prev + 1));
      await axios.post(`/api/institutions/follow/${institution.id}/`);
      openSnackbar({
        open: true,
        message: wasFollowed
          ? `Vous ne suivez plus ${institution.name}`
          : `Vous suivez maintenant ${institution.name}`,
        variant: "alert",
        alert: { color: wasFollowed ? "error" : "success" },
        autoHideDuration: 4000,
      });
    } catch (error) {
      setIsFollowed(wasFollowed);
      setFollowerCount(safeTotalFollowers);
      console.error("Error toggling follow status:", error);
      openSnackbar({
        open: true,
        message: "Une erreur s'est produite lors de la modification du suivi",
        variant: "alert",
        alert: { color: "error" },
        autoHideDuration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.article
      {...cardMotion}
      className="f2-card in-card"
      onClick={() => onOpen?.(institution.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(institution.id);
      }}
    >
      <CardActionButtons
        id={institution.id}
        editType="institution"
        size="medium"
        className="in-edit"
      />

      <div className="f2-card-media is-logo">
        <img
          src={logoSrc}
          alt=""
          onError={(e) => {
            e.target.src = institutionImages[7];
          }}
        />
      </div>

      <div className="in-card-body">
        <h3 className="in-card-title">
          <HighlightText text={institution.name} query={highlightQuery} />
        </h3>

        {institution.organization_type?.name && (
          <span className="in-card-type">
            <HighlightText
              text={institution.organization_type.name}
              query={highlightQuery}
            />
          </span>
        )}

        {institution.resume && (
          <p className="in-card-resume">
            <HighlightText text={institution.resume} query={highlightQuery} />
          </p>
        )}

        <div className="in-card-foot">
          <div className="in-card-meta">
            <span>
              <HighlightText
                text={institution.city || "—"}
                query={highlightQuery}
              />
            </span>
            <span>
              {followerCount} abonné{followerCount !== 1 ? "s" : ""}
            </span>
          </div>
          <button
            type="button"
            className={`in-follow-btn${isFollowed ? " is-following" : " is-idle"}`}
            onClick={handleFollowToggle}
            disabled={isLoading}
          >
            {isLoading ? (
              "Chargement…"
            ) : isFollowed ? (
              <>
                <UserMinus2 size={16} />
                Abonné
              </>
            ) : (
              <>
                <UserPlus2 size={16} />
                Suivre
              </>
            )}
          </button>
        </div>
      </div>
    </motion.article>
  );
}
